import { onMounted, onUnmounted, ref } from 'vue'

const POLL_INTERVAL_MS = 60_000
const REQUEST_TIMEOUT_MS = 10_000
const isBuildVersion = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{13}$/.test(value)

// version.json is a frontend build artifact, not an authenticated backend API.
export function readBuildVersion(payload: unknown): string {
  if (payload && typeof payload === 'object' && 'version' in payload && isBuildVersion(payload.version)) {
    return payload.version
  }
  throw new TypeError('Invalid build version payload')
}

export function useBuildVersionCheck() {
  // Compare with this running bundle, never with another tab's localStorage.
  const currentVersion = import.meta.env.VITE_BUILD_VERSION
  const isUpdateAvailable = ref(false)
  const latestVersion = ref<string | null>(null)
  let active = false
  let timer: ReturnType<typeof setInterval> | undefined
  let timeout: ReturnType<typeof setTimeout> | undefined
  let request: AbortController | null = null

  function stop() {
    clearInterval(timer)
    clearTimeout(timeout)
    document.removeEventListener('visibilitychange', checkVersion)
    window.removeEventListener('online', checkVersion)
  }

  async function checkVersion() {
    if (!active || request || isUpdateAvailable.value || document.visibilityState !== 'visible' || !navigator.onLine) return
    const controller = new AbortController()
    request = controller
    timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      if (!response.ok) return
      const version = readBuildVersion(await response.json())
      if (active && !controller.signal.aborted && version !== currentVersion) {
        latestVersion.value = version
        isUpdateAvailable.value = true
        stop()
      }
    } catch {
      // Offline, a partial deployment or invalid JSON must not interrupt work.
      // The next interval (or returning to the tab) retries the check.
    } finally {
      clearTimeout(timeout)
      request = null
    }
  }

  function reloadPage() {
    window.location.reload()
  }

  onMounted(() => {
    if (!import.meta.env.PROD || !isBuildVersion(currentVersion)) return
    active = true
    timer = setInterval(checkVersion, POLL_INTERVAL_MS)
    document.addEventListener('visibilitychange', checkVersion)
    window.addEventListener('online', checkVersion)
    void checkVersion()
  })

  onUnmounted(() => {
    active = false
    stop()
    request?.abort()
  })

  return { isUpdateAvailable, latestVersion, reloadPage }
}
